import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { isApiError, unwrap } from "@brad-tipper/app-frontend";

import { api, sessionStore } from "~/api/client";
import type { Session as ApiSession, User } from "~/api/generated/types.gen";
import { pendingMfa } from "~/api/mfa-pending";
import { useAsyncAction } from "~/hooks/useAsyncAction";
import { getAppUrl } from "~/utils/app-url";
import { getAuthCallbackUrl } from "~/utils/auth";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  authProviders: string[];
  hasPassword: boolean;
  firstName: string;
  lastName: string;
  subscriptionTier: "free" | "pro";
  subscriptionStatus: "active" | "unsubscribed";
  subscriptionEventAt: string | null;
  mfaEnabled: boolean;
  units: "metric" | "imperial";
  dateFormat: string;
  timezone: string;
  preferences: Record<string, unknown> | null;
}

/** Just enough of a session for route guards: who is signed in. */
export type AuthSession = { user: { id: string; email: string } };

type AuthContextValue = {
  session: AuthSession | null;
  user: AuthUser | null;
  loading: boolean;
  error: Error | null;
  /** Kept for the route guards; password reset is a signed-out token flow now. */
  passwordRecoveryActive: boolean;
  isAuthenticated: boolean;
  refresh: () => Promise<AuthUser | null>;
  login: (input: {
    email: string;
    password: string;
    captchaToken?: string;
  }) => Promise<void>;
  loginPending: boolean;
  loginError: Error | null;
  register: (input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    country?: string;
    captchaToken?: string;
    callbackUrl?: string;
  }) => Promise<{ signedIn: boolean }>;
  registerPending: boolean;
  registerError: Error | null;
  logout: () => Promise<void>;
  logoutPending: boolean;
  magicLink: (email: string) => Promise<void>;
  magicLinkPending: boolean;
  forgotPassword: (input: {
    email: string;
    captchaToken?: string;
  }) => Promise<void>;
  forgotPasswordPending: boolean;
  forgotPasswordError: Error | null;
  resetPassword: (input: { token: string; password: string }) => Promise<void>;
  resetPasswordPending: boolean;
  setPassword: (input: { password: string }) => Promise<void>;
  setPasswordPending: boolean;
  updateProfile: (input: {
    firstName?: string;
    lastName?: string;
    units?: "metric" | "imperial";
    dateFormat?: string;
    timezone?: string;
  }) => Promise<void>;
  updateProfilePending: boolean;
  updatePreferences: (input: Record<string, unknown>) => Promise<void>;
  updatePreferencesPending: boolean;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void>;
  changePasswordPending: boolean;
  /** Signs in with the token from an emailed magic link or confirmation link. */
  completeEmailLink: (input: {
    token: string;
    type: "magiclink" | "signup";
  }) => Promise<void>;
  /** Completes a sign-in that stopped at the authenticator-code step. */
  completeMfaSignIn: (input: {
    code?: string;
    recoveryCode?: string;
  }) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_CACHE_KEY = "florafolio.user";

function toAuthUser(user: User): AuthUser {
  const preferences =
    user.preferences &&
    typeof user.preferences === "object" &&
    !Array.isArray(user.preferences)
      ? (user.preferences as Record<string, unknown>)
      : null;
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    image: null,
    authProviders: ["password"],
    // Every account is created with email + password.
    hasPassword: true,
    firstName: user.firstName,
    lastName: user.lastName,
    subscriptionTier: user.subscriptionTier,
    subscriptionStatus: user.subscriptionStatus,
    subscriptionEventAt: user.subscriptionEventAt,
    mfaEnabled: user.mfaEnabled,
    units: user.units,
    dateFormat: user.dateFormat,
    timezone: user.timezone,
    preferences,
  };
}

const toSession = (user: AuthUser): AuthSession => ({
  user: { id: user.id, email: user.email },
});

async function cacheUser(user: AuthUser | null) {
  try {
    if (user) await AsyncStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else await AsyncStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // The cache only speeds up an offline start.
  }
}

async function readCachedUser(): Promise<AuthUser | null> {
  try {
    const raw = await AsyncStorage.getItem(USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

/** Server error codes the screens show in their own words. */
function friendlyError(error: unknown): Error {
  if (isApiError(error) && error.code === "invalid_credentials") {
    return new Error("Current password is incorrect.");
  }
  return error instanceof Error ? error : new Error("Request failed.");
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const session = useMemo(() => (user ? toSession(user) : null), [user]);

  /** Persists a new token pair and adopts its user. */
  const adopt = useCallback(async (next: ApiSession) => {
    await sessionStore.set({
      accessToken: next.accessToken,
      refreshToken: next.refreshToken,
      expiresAt: next.expiresAt,
    });
    const nextUser = toAuthUser(next.user);
    setUser(nextUser);
    setError(null);
    void cacheUser(nextUser);
    return nextUser;
  }, []);

  const refresh = useCallback(async () => {
    if (!sessionStore.get()) return null;
    const me = await unwrap(api.getMe());
    const nextUser = toAuthUser(me);
    setUser(nextUser);
    setError(null);
    void cacheUser(nextUser);
    return nextUser;
  }, []);

  useEffect(() => {
    let active = true;

    void (async () => {
      const stored = await sessionStore.load();
      if (!stored) {
        if (active) setLoading(false);
        return;
      }
      // Render from the cached user straight away (also what makes an offline start work),
      // then confirm with the server.
      const cached = await readCachedUser();
      if (active && cached) setUser(cached);
      try {
        await refresh();
      } catch (loadError) {
        if (!active) return;
        if (isApiError(loadError) && loadError.status === 401) {
          await sessionStore.clear();
        } else if (!cached) {
          setError(
            loadError instanceof Error
              ? loadError
              : new Error("Unable to load authentication."),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    // A rejected refresh token clears the session: that is a sign-out.
    const unsubscribe = sessionStore.subscribe((stored) => {
      if (!active || stored) return;
      setUser(null);
      void cacheUser(null);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [refresh]);

  const login = useAsyncAction(
    async (input: {
      email: string;
      password: string;
      captchaToken?: string;
    }) => {
      const result = await unwrap(
        api.signIn({
          body: {
            email: input.email,
            password: input.password,
            captchaToken: input.captchaToken,
          },
        }),
      );
      if (result.mfaRequired && result.mfaToken) {
        // MfaGate takes over until the authenticator code is entered.
        pendingMfa.set(result.mfaToken);
        return;
      }
      if (result.session) await adopt(result.session);
    },
  );

  const completeMfaSignIn = useCallback(
    async (input: { code?: string; recoveryCode?: string }) => {
      const mfaToken = pendingMfa.get();
      if (!mfaToken) throw new Error("Sign in again to continue.");
      const result = await unwrap(
        api.verifyMfa({
          body: {
            mfaToken,
            code: input.code?.trim(),
            recoveryCode: input.recoveryCode?.trim(),
          },
        }),
      );
      if (result.session) await adopt(result.session);
      pendingMfa.set(null);
    },
    [adopt],
  );

  const completeEmailLink = useCallback(
    async (input: { token: string; type: "magiclink" | "signup" }) => {
      const result = await unwrap(
        input.type === "signup"
          ? api.confirmEmail({ body: { token: input.token } })
          : api.verifyMagicLink({ body: { token: input.token } }),
      );
      if (result.mfaRequired && result.mfaToken) {
        pendingMfa.set(result.mfaToken);
        return;
      }
      if (result.session) await adopt(result.session);
    },
    [adopt],
  );

  const register = useAsyncAction(
    async (input: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      country?: string;
      captchaToken?: string;
      callbackUrl?: string;
    }) => {
      const result = await unwrap(
        api.signUp({
          body: {
            email: input.email,
            password: input.password,
            name: `${input.firstName} ${input.lastName}`.trim(),
            country: input.country?.trim().toUpperCase() || undefined,
            redirectUrl: getAuthCallbackUrl(input.callbackUrl),
            captchaToken: input.captchaToken,
          },
        }),
      );
      // With email confirmation on there is no session until the link is followed;
      // the caller shows the "check your email" state in that case.
      if (result.session) await adopt(result.session);
      return { signedIn: Boolean(result.session) };
    },
  );

  const logout = useAsyncAction(async () => {
    try {
      if (sessionStore.get()) await api.signOut();
    } catch {
      // Signing out locally is what matters.
    }
    pendingMfa.set(null);
    await sessionStore.clear();
    setUser(null);
    void cacheUser(null);
  });

  const magicLink = useAsyncAction(async (email: string) => {
    await unwrap(
      api.requestMagicLink({
        body: { email, redirectUrl: getAppUrl("/auth/callback") },
      }),
    );
  });

  const forgotPassword = useAsyncAction(
    async (input: { email: string; captchaToken?: string }) => {
      await unwrap(
        api.forgotPassword({
          body: {
            email: input.email,
            redirectUrl: getAppUrl("/reset-password"),
            captchaToken: input.captchaToken,
          },
        }),
      );
    },
  );

  const resetPassword = useAsyncAction(
    async (input: { token: string; password: string }) => {
      await unwrap(api.resetPassword({ body: input }));
    },
  );

  // Accounts are always created with a password, so there is never one to "set".
  const setPassword = useAsyncAction(async (_input: { password: string }) => {
    throw new Error("This account already has a password.");
  });

  const updateProfile = useAsyncAction(
    async (input: {
      firstName?: string;
      lastName?: string;
      units?: "metric" | "imperial";
      dateFormat?: string;
      timezone?: string;
    }) => {
      if (!sessionStore.get()) throw new Error("You must be signed in.");
      const name = [input.firstName, input.lastName].filter(Boolean).join(" ");
      const me = await unwrap(
        api.updateMe({
          body: {
            name: name || undefined,
            units: input.units,
            dateFormat: input.dateFormat,
            timezone: input.timezone,
          },
        }),
      );
      const nextUser = toAuthUser(me);
      setUser(nextUser);
      void cacheUser(nextUser);
    },
  );

  const changePassword = useAsyncAction(
    async (input: { currentPassword: string; newPassword: string }) => {
      try {
        await unwrap(api.changePassword({ body: input }));
      } catch (changeError) {
        throw friendlyError(changeError);
      }
    },
  );

  const updatePreferences = useAsyncAction(
    async (input: Record<string, unknown>) => {
      if (!sessionStore.get()) throw new Error("You must be signed in.");
      const me = await unwrap(api.updateMe({ body: { preferences: input } }));
      const nextUser = toAuthUser(me);
      setUser(nextUser);
      void cacheUser(nextUser);
    },
  );

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        error,
        passwordRecoveryActive: false,
        isAuthenticated: Boolean(session),
        refresh,
        login: login.run,
        loginPending: login.pending,
        loginError: login.error,
        register: register.run,
        registerPending: register.pending,
        registerError: register.error,
        logout: logout.run,
        logoutPending: logout.pending,
        magicLink: magicLink.run,
        magicLinkPending: magicLink.pending,
        forgotPassword: forgotPassword.run,
        forgotPasswordPending: forgotPassword.pending,
        forgotPasswordError: forgotPassword.error,
        resetPassword: resetPassword.run,
        resetPasswordPending: resetPassword.pending,
        setPassword: setPassword.run,
        setPasswordPending: setPassword.pending,
        updateProfile: updateProfile.run,
        updateProfilePending: updateProfile.pending,
        updatePreferences: updatePreferences.run,
        updatePreferencesPending: updatePreferences.pending,
        changePassword: changePassword.run,
        changePasswordPending: changePassword.pending,
        completeMfaSignIn,
        completeEmailLink,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider.");
  return context;
}
